import React from "react";
import EdiStatusBadge from "./EdiStatusBadge";
import {
  asArray,
  eventTimestamp,
  formatTimestamp,
  importedTimestamp,
  PLACEHOLDER,
  readableCode,
  shown,
} from "../../utils/ediPresentation";

function EvidenceCell({ row }) {
  const types = asArray(row.transport_evidence_types);
  const statuses = asArray(row.source_evidence_statuses);
  const count = Number.isInteger(row.transport_evidence_count) ? row.transport_evidence_count : null;
  if (count === null && !types.length && !statuses.length) return PLACEHOLDER;
  return <div className="space-y-1">
    {count !== null && <div>{count} Transport {count === 1 ? "Record" : "Records"}</div>}
    <div className="flex flex-wrap gap-1">
      {types.map((value) => <EdiStatusBadge key={`type-${value}`} value={value} label={value} />)}
      {statuses.map((value) => <EdiStatusBadge key={`status-${value}`} value={value} />)}
    </div>
  </div>;
}

function AssessmentCell({ row }) {
  const sourceLinks = asArray(row.matched_acknowledgement_message_ids);
  const acknowledged = asArray(row.acknowledged_source_message_ids);
  const statuses = asArray(row.assessment_statuses);
  const business = asArray(row.business_statuses);
  const badges = business.length ? business.map((value) => <EdiStatusBadge key={value} value={value} label={value} />) : statuses.map((value) => <EdiStatusBadge key={value} value={value} />);
  if (sourceLinks.length) return <div className="space-y-1"><div className="flex flex-wrap gap-1">{badges}</div><div>Matched {"\u2192"} Message #{sourceLinks[0]}</div></div>;
  if (acknowledged.length) return <div className="space-y-1"><div>Acknowledges {acknowledged.length} {acknowledged.length === 1 ? "Message" : "Messages"}</div><div className="flex flex-wrap gap-1">{badges}</div></div>;
  if (business.length) return <div className="flex flex-wrap gap-1">{badges}</div>;
  if (statuses.length) return <div className="flex flex-wrap gap-1">{statuses.map((value) => <EdiStatusBadge key={value} value={value} />)}</div>;
  return PLACEHOLDER;
}

export default function EdiMessageTable({ rows, onView }) {
  return (
    <div className="overflow-auto rounded border bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50">
          <tr>{["Event Time", "Imported", "Customer", "Partner", "Direction", "Documents", "Source", "Evidence", "997 Status", "ISA Control", "Validation", "Status", "Actions"].map((heading) => <th scope="col" className="whitespace-nowrap p-2 text-left" key={heading}>{heading}</th>)}</tr>
        </thead>
        <tbody>{rows.map((row) => {
          const historical = Boolean(row.historical_event_at);
          const source = row.source_type ?? null;
          const run = row.backfill_run_uuid;
          return <tr className="border-t align-top" key={row.id}>
            <td className="whitespace-nowrap p-2"><div>{formatTimestamp(eventTimestamp(row))}</div><div className="text-xs text-slate-500" title={row.event_at_source || "INGESTED_AT"}>{historical ? "Historical" : "Ingested fallback"}</div></td>
            <td className="whitespace-nowrap p-2"><div>{formatTimestamp(importedTimestamp(row))}</div><div className="text-xs text-slate-500">Import time</div></td>
            <td className="p-2">{shown(row.cust_code)}</td>
            <td className="p-2">{shown(row.partner_code)}</td>
            <td className="p-2">{row.direction ? <EdiStatusBadge value={row.direction} /> : PLACEHOLDER}</td>
            <td className="p-2">{asArray(row.transaction_types).length ? asArray(row.transaction_types).map((value) => <span className="mr-1 inline-block font-mono" key={value}>{value}</span>) : PLACEHOLDER}</td>
            <td className="p-2"><div>{source ? readableCode(source) : PLACEHOLDER}</div>{run && <span title={run} className="font-mono text-xs text-slate-500">{run.slice(0, 8)}{"\u2026"}</span>}</td>
            <td className="p-2"><EvidenceCell row={row} /></td>
            <td className="p-2"><AssessmentCell row={row} /></td>
            <td className="p-2 font-mono">{shown(row.interchange_control_number)}</td>
            <td className="p-2">{row.has_errors ? `${row.validation_error_count || 0}${row.validation_error_count_truncated ? "+" : ""} errors` : "Valid"}</td>
            <td className="p-2">{row.parse_status ? <EdiStatusBadge value={row.parse_status} /> : PLACEHOLDER}</td>
            <td className="p-2"><button type="button" className="whitespace-nowrap text-blue-700 underline-offset-2 hover:underline" onClick={() => onView(row.id)}>View Details</button></td>
          </tr>;
        })}</tbody>
      </table>
    </div>
  );
}
